import { t } from '@nodal/i18n';
import { Button } from '@nodal/uikit/components/Button';
import { ButtonLoading } from '@nodal/uikit/components/ButtonLoading';
import { TextInput } from '@nodal/uikit/components/TextInput';
import { Form, Formik } from 'formik';
import { forwardRef } from 'react';
import * as Yup from 'yup';

import type {
  RejectionFormProps,
  RejectReasonQuestions,
} from './RejectionForm.interface';

// Keep the old options for backward compatibility and data population
const rejectReasonQuestions: RejectReasonQuestions[] = [
  {
    value: 'lifestyles_dont_match',
    label: t("Our lifestyles don't match"),
  },
  {
    value: 'too_far_away',
    label: t('Surrogate/parents are too far away'),
  },
  {
    value: 'journey_preferences_dont_match',
    label: t("Our surrogacy journey preferences don't match"),
  },
];

const validationSchema = Yup.object({
  other_answer: Yup.string().required(
    t('Please provide a reason for rejection'),
  ),
});

export const RejectionForm = forwardRef<HTMLButtonElement, RejectionFormProps>(
  ({ onConfirm, onCancel, initialValues }, buttonRef) => {
    // Convert old enum values to text for backward compatibility
    const getInitialTextValue = () => {
      if (initialValues.other_answer) {
        return initialValues.other_answer;
      }

      if (
        initialValues.passing_reason &&
        initialValues.passing_reason !== 'other'
      ) {
        const oldOption = rejectReasonQuestions.find(
          (option) => option.value === initialValues.passing_reason,
        );
        return oldOption?.label || '';
      }

      return '';
    };

    const handleSubmit = (values: { other_answer: string }) => {
      // Always save as "other" with the text answer for consistency
      onConfirm({
        passing_reason: 'other',
        other_answer: values.other_answer,
      });
    };

    return (
      <Formik
        initialValues={{
          other_answer: getInitialTextValue(),
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {({ isSubmitting }) => (
          <Form className="relative">
            <div className="flex flex-col py-4 px-8 border-b border-grey-forest-200">
              <div>
                <TextInput
                  name="other_answer"
                  type="text"
                  className="w-full"
                  placeholder={t('Please provide your reason for rejection')}
                  label={t('What is your reason for passing on this request?*')}
                  required
                />
              </div>

              <p className="text-xs leading-6 text-grey-forest-600">
                {t(
                  `* your feedback is used solely to improve our product and the matching process, it will not be shared with the surrogate or anyone outside of Nodal`,
                )}
              </p>
            </div>

            <div className="flex gap-2 justify-end py-3 px-6">
              <Button
                variant="tertiary"
                type="button"
                onClick={onCancel}
                ref={buttonRef}
              >
                {t('Cancel')}
              </Button>
              <ButtonLoading
                variant="primary"
                type="submit"
                loading={isSubmitting}
              >
                {t('Yes, reject')}
              </ButtonLoading>
            </div>
          </Form>
        )}
      </Formik>
    );
  },
);

RejectionForm.displayName = 'RejectionForm';
